<template>
    <div>
        <!-- Cause list -->
        <q-table
            @request="onRequest"
            :filter="table.search"
            :rows="causes"
            :columns="table.cols"
            v-model:pagination="table.pagination"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            :no-data-label="$t('No data available')"
            :class="$q.screen.gt.lg ? 'sticky-table cause-table ' : ''"
            row-key="id"
            binary-state-sort
            flat
            :grid="$q.screen.lt.lg"
        >
            <!-- top bar -->
            <template v-slot:top>
                <!-- title -->
                <div class="col-12">
                    <div class="text-h4 text-weight-medium">
                        {{ $t('Downtime Causes') }}
                    </div>
                    <div class="page-description">
                        {{
                            $t('Manage downtime causes for {facilityName}.', {
                                facilityName: facility?.name
                            })
                        }}
                    </div>
                </div>
                <!-- search bar -->
                <div
                    class="row fit q-mb-sm"
                    :class="
                        $q.screen.lt.sm
                            ? ''
                            : 'wrap justify-between items-start content-start'
                    "
                >
                    <q-input
                        v-model="table.search"
                        @clear="removeFilteredCauseFromStore"
                        :placeholder="$t('Search downtime cause')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        debounce="750"
                        clearable
                        outlined
                        dense
                        color="accent"
                        @input="setFilteredCauseInStore"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <!-- "Add cause" button -->
                    <q-btn
                        @click="
                            $router.push({
                                name: 'cause_create',
                                params: {
                                    facilityId: facility.id
                                }
                            })
                        "
                        :label="$t('Add cause')"
                        :disable="$userCannot('create', 'causes')"
                        class="table-header__button"
                        :class="$q.screen.lt.sm ? 'col-12' : ''"
                        color="accent"
                        size="md"
                        id="add-cause-btn"
                    ></q-btn>
                </div>
            </template>

            <!-- Type of loss column -->
            <template v-slot:body-cell-type_of_loss="props">
                <q-td :props="props">
                    <q-badge
                        outline
                        align="middle"
                        :color="getLossOptions(props.row.type_of_loss).color"
                        >{{
                            $t(getLossOptions(props.row.type_of_loss).label)
                        }}</q-badge
                    >
                </q-td>
            </template>

            <!-- Planned column -->
            <template v-slot:body-cell-planned="props">
                <q-td :props="props">
                    <q-icon
                        :name="
                            props.row.planned
                                ? 'fas fa-check-circle'
                                : 'fas fa-times-circle'
                        "
                        :color="props.row.planned ? 'positive' : 'negative'"
                    ></q-icon>
                </q-td>
            </template>

            <!-- Equipment failure column -->
            <template v-slot:body-cell-equipment_failure="props">
                <q-td :props="props">
                    <q-icon
                        :name="
                            props.row.equipment_failure
                                ? 'fas fa-check-circle'
                                : 'fas fa-times-circle'
                        "
                        :color="
                            props.row.equipment_failure
                                ? 'positive'
                                : 'negative'
                        "
                    ></q-icon>
                </q-td>
            </template>

            <!-- actions column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'cause_update',
                                    params: {
                                        facilityId: facility.id,
                                        causeId: props.row.id
                                    },
                                    state: {
                                        name: props.row.name,
                                        code: props.row.code,
                                        description: props.row.description,
                                        planned: props.row.planned,
                                        target_down_time:
                                            props.row.target_down_time,
                                        category: props.row.category,
                                        category_name: props.row.category_name,
                                        type_of_loss: props.row.type_of_loss,
                                        equipment_failure:
                                            props.row.equipment_failure,
                                        lock_transition_to_running:
                                            props.row.lock_transition_to_running
                                    }
                                })
                            "
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                            outline
                        />
                        <div>
                            <q-btn
                                @click="remove(props.row)"
                                :disable="$userCannot('delete', 'causes')"
                                :color="
                                    $userCannot('delete', 'causes')
                                        ? 'grey'
                                        : 'negative'
                                "
                                class="table__action-button"
                                icon="fas fa-trash-alt"
                                outline
                            ></q-btn>
                            <q-tooltip v-if="$userCannot('edit', 'causes')">
                                {{
                                    $t(
                                        'You do not have permission to perform this action.'
                                    )
                                }}
                            </q-tooltip>
                        </div>
                    </div>
                </q-td>
            </template>

            <!-- Card template -->
            <template v-slot:item="props">
                <cause-card
                    @edit="
                        $router.push({
                            name: 'cause_update',
                            params: {
                                facilityId: facility.id,
                                causeId: props.row.id
                            },
                            state: {
                                name: props.row.name,
                                code: props.row.code,
                                description: props.row.description,
                                planned: props.row.planned,
                                target_down_time: props.row.target_down_time,
                                category: props.row.category,
                                category_name: props.row.category_name,
                                type_of_loss: props.row.type_of_loss,
                                equipment_failure: props.row.equipment_failure,
                                lock_transition_to_running:
                                    props.row.lock_transition_to_running
                            }
                        })
                    "
                    @delete="remove(props.row)"
                    :name="props.row.name"
                    :lossType="props.row.type_of_loss"
                    :categoryName="props.row.category_name"
                    :planned="props.row.planned"
                    :description="props.row.description"
                    :targetDowntime="props.row.target_down_time"
                    :equipmentFailure="props.row.equipment_failure"
                ></cause-card>
            </template>
        </q-table>
    </div>
</template>

<script>
import CauseCard from '@components/CauseCard.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { getLossOptions } from '@models/causes.js';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFiltersStore } from '@stores/filters.js';

export default {
    name: 'CauseList',
    components: { CauseCard },
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Downtime Causes')
            }
        ]);
        // get the store filtered cause name
        const storedFilteredCause = this.getStoredFacilityFilter({
            facilityId: this.facilityId,
            filter: 'causes'
        });

        // sync the component's search value with the stored
        // fitlered cause string.
        this.table.search = storedFilteredCause;
        this.onRequest({
            pagination: this.table.pagination,
            filter: ''
        });
    },
    data() {
        return {
            baseURL: 'causes/',
            causes: [],
            // table configs
            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'type_of_loss',
                        name: 'type_of_loss',
                        label: this.$t('Type of Loss'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'category_name',
                        name: 'category',
                        label: this.$t('Category'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true,
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('Code'),
                        sortable: true,
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'planned',
                        name: 'planned',
                        label: this.$t('Planned'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'equipment_failure',
                        name: 'equipment_failure',
                        label: this.$t('Equipment failure'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'target_down_time',
                        name: 'target_down_time',
                        label: this.$t('Target downtime (min)'),
                        format: (val, row) => {
                            if (!row.planned) {
                                return '-';
                            }
                            return val;
                        }
                    },
                    {
                        align: 'left',
                        field: 'description',
                        name: 'description',
                        label: this.$t('Description'),
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            }
        };
    },
    computed: {
        ...mapState(useFiltersStore, ['getStoredFacilityFilter']),
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.$store.getters['facilities/getFacility'](
                this.facilityId
            );
        },
        /**
         * Get URL with query parameters.
         * @return {String}
         */
        url() {
            // get current pagination state attributes
            let { descending, page, sortBy } = this.table.pagination;

            // order by category name
            if (sortBy === 'category') {
                sortBy = 'category__name';
            }

            // update URL
            return this.updateURL({
                baseURL: this.baseURL,
                params: {
                    search: this.table.search,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    facility: this.facilityId
                }
            });
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFiltersStore, ['setFacilityFilter']),
        getLossOptions,
        /**
         * Get causes from the server.
         */
        async getCauses() {
            await this.$axios
                .get(this.url)
                .then(response => {
                    // push data
                    this.causes.push(...response.data.results);
                    // update total items count
                    this.table.pagination.rowsNumber = response.data.count;
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to load causes. Please try again later.'
                        ),
                        error
                    );
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Delete cause.
         * @param {Object} row - Cause from table row.
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this cause?')
            ).onOk(async () => {
                // begin updating cause
                this.beginLoading(`${this.$t('Deleting cause')}...`);
                await this.$axios
                    .delete(`${this.baseURL}${row.id}/`)
                    .then(response => {
                        // remove cause from product list
                        let causeIndex = this.causes.findIndex(
                            cause => cause.id === row.id
                        );
                        this.causes.splice(causeIndex, 1);

                        // notify user
                        this.$notifySuccess(this.$t('Downtime cause deleted'));
                    })
                    .catch(error => {
                        this.$notifyError(
                            this.$t(
                                'Failed to remove cause. Please try again later.'
                            ),
                            error
                        );
                    })
                    .finally(() => {
                        // stop loading
                        this.stopLoading();
                    });
            });
        },
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         * @param {string} props.filter
         */
        async onRequest(props) {
            // update pagination object
            this.table.pagination = props.pagination;

            // begin loading
            this.beginLoading(`${this.$t('Loading causes')}...`);

            // clear existing data
            this.causes = [];

            // fetch data from the server
            await this.getCauses();
        },

        /**
         * Update the filtered cause for particular facility,
         * in the store.
         * Depends on the value of table.search
         */
        setFilteredCauseInStore() {
            this.setFacilityFilter({
                facilityId: this.facilityId,
                filter: 'causes',
                value: this.table.search
            });
        },

        /**
         * Remove the filtered cause of per facility from the store.
         * And reset the search key of this.table
         * to its initial value.
         */
        removeFilteredCauseFromStore() {
            this.setFacilityFilter({
                facilityId: this.facilityId,
                filter: 'causes',
                value: ''
            });
            this.table.search = '';
        }
    }
};
</script>

<style lang="scss" scoped>
// align table top with cards
:deep(.q-table__top) {
    padding: 0px;
}

.cause-table {
    height: 75vh;
}
</style>
